import { varsHash } from "@framework/relay/server/network/helpers/create-hash";
import Middleware, { LogContext } from "@framework/relay/shared/Middleware";

/**
 * Options for providing community data in log context
 */
interface LogContextMiddlewareOptions {
  /**
   * Community data resolver - can be either:
   * 1. A function that resolves data from communityId (for dynamic resolution)
   * 2. A static community data object (for zero-overhead when data is known upfront)
   *
   * Server context with dynamic resolution function:
   * @example
   * communityDataResolver: (communityId) => {
   *   const config = getCommunityConfig(communityId);
   *   return {
   *     communityId,
   *     communityName: config.communityName,
   *     communityApiOrigin: config.communityApiOrigin,
   *     communityOrigin: config.communityOrigin,
   *     communityWpSiteId: config.communityWpSiteId,
   *   };
   * }
   *
   * Browser context with static data:
   * @example
   * communityDataResolver: { communityId: 1 }
   */
  communityDataResolver?:
    | ((communityId: number) => LogContext["community"] | undefined)
    | LogContext["community"];
}

/**
 * LogContextMiddleware - Builds structured logging context from available request data
 *
 * Runs EARLY in the middleware chain because all necessary data is available:
 * - ctx.request.id (persisted query MD5 hash)
 * - ctx.operation.operationName
 * - ctx.operation.variables
 * - ctx.incomingRequestContext.communityId
 *
 * This context is then available to ALL downstream middleware via ctx.logContext,
 * including logMiddleware which can spread this context into its logs.
 *
 * @param options - Optional configuration for resolving community data
 */
export default function createLogContextMiddleware(
  options?: LogContextMiddlewareOptions,
): Middleware {
  const { communityDataResolver } = options || {};

  return (next) => async (ctx) => {
    // Build structured log context from data available early in the request
    const communityId = ctx.incomingRequestContext?.communityId;

    let communityData: LogContext["community"] = undefined;

    if (communityDataResolver) {
      if (typeof communityDataResolver === "function") {
        // Dynamic resolution: call the function with communityId
        if (communityId) {
          try {
            communityData = communityDataResolver(communityId);
          } catch {
            // Fallback if resolution fails
            communityData = { communityId };
          }
        }
      } else {
        // Static data: use as-is
        communityData = communityDataResolver;
      }
    } else if (communityId) {
      // No resolver provided - just track ID from context
      communityData = { communityId };
    }

    // Build query context from data available early
    const queryData: LogContext["query"] = {
      operationName: ctx.operation.operationName,
      variables: ctx.operation.variables, // Include the actual variables
      variableHash:
        Object.keys(ctx.operation.variables).length > 0
          ? await varsHash(ctx.operation.variables)
          : null, // Can calculate early from ctx.operation.variables
      persistedQuery: {
        md5Hash: ctx.request.id || undefined, // Internal Relay identifier (MD5)
        // sha256Hash will be added later by APQ middleware if needed
      },
    };

    /**
     * Add structured log context to middleware context
     * This will be available to ALL downstream middleware
     * Note: Network context is handled by the relay logger itself
     */
    ctx.logContext = {
      query: queryData,
      community: communityData,
    };

    return next(ctx);
  };
}
